import api from './api';

export interface LibroDiarioListItem {
  nroAsiento: number;
  fechaAsiento: string | null;
  nroIdformulario: number;
  /** Ej.: «FC A-0004-00012345»; asiento manual: «—». */
  comprobante?: string;
  aclaracion: string | null;
  estaContabilizado: boolean;
  totalDebe: number;
  totalHaber: number;
  diferencia: number;
  balanceado: boolean;
}

export interface LibroDiarioSearchParams {
  fechaDesde?: string;
  fechaHasta?: string;
  nroAsiento?: number;
  searchTerm?: string;
  pageNumber: number;
  pageSize: number;
}

/** Filtros para el PDF de listado (sin paginación). */
export interface LibroDiarioPdfParams {
  fechaDesde?: string;
  fechaHasta?: string;
  nroAsiento?: number;
  searchTerm?: string;
}

export interface LibroDiarioPagedResult {
  items: LibroDiarioListItem[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface DeLiDiLineaDto {
  nroId: number;
  nroCuenta: number | null;
  nomCuenta: string | null;
  detalle: string | null;
  debe: number | null;
  haber: number | null;
  nroLinea: number | null;
}

export interface LibroDiarioDetalle {
  nroAsiento: number;
  fechaAsiento: string | null;
  nroIdformulario: number;
  /** Misma leyenda que en listado (ej. FC A-0004-00012345); manual: «—». */
  comprobante?: string;
  aclaracion: string | null;
  contabilizado: string | null;
  estaContabilizado: boolean;
  esManual: boolean;
  totalDebe: number;
  totalHaber: number;
  lineas: DeLiDiLineaDto[];
}

export interface DeLiDiLineaWriteDto {
  nroCuenta: number;
  detalle?: string | null;
  debe: number;
  haber: number;
}

export interface LibroDiarioCreateDto {
  fechaAsiento: string;
  aclaracion?: string | null;
  lineas: DeLiDiLineaWriteDto[];
}

function unwrapMessage(error: unknown): string {
  const ax = error as { response?: { data?: { message?: string } } };
  const m = ax.response?.data?.message;
  return typeof m === 'string' ? m : 'Error en la operación';
}

const libroDiarioService = {
  getPaged: async (params: LibroDiarioSearchParams): Promise<LibroDiarioPagedResult> => {
    const response = await api.get<LibroDiarioPagedResult>('/api/LibroDiario', {
      params: {
        fechaDesde: params.fechaDesde || undefined,
        fechaHasta: params.fechaHasta || undefined,
        nroAsiento: params.nroAsiento ?? undefined,
        searchTerm: params.searchTerm?.trim() || undefined,
        pageNumber: params.pageNumber,
        pageSize: params.pageSize
      }
    });
    return response.data;
  },

  getById: async (nroAsiento: number): Promise<LibroDiarioDetalle> => {
    const response = await api.get<LibroDiarioDetalle>(`/api/LibroDiario/${nroAsiento}`);
    return response.data;
  },

  create: async (dto: LibroDiarioCreateDto): Promise<LibroDiarioDetalle> => {
    const response = await api.post<LibroDiarioDetalle>('/api/LibroDiario', dto);
    return response.data;
  },

  update: async (nroAsiento: number, dto: LibroDiarioCreateDto): Promise<void> => {
    await api.put(`/api/LibroDiario/${nroAsiento}`, dto);
  },

  delete: async (nroAsiento: number): Promise<void> => {
    await api.delete(`/api/LibroDiario/${nroAsiento}`);
  },

  contabilizar: async (nroAsiento: number): Promise<void> => {
    await api.patch(`/api/LibroDiario/${nroAsiento}/contabilizar`);
  },

  descontabilizar: async (nroAsiento: number): Promise<void> => {
    await api.patch(`/api/LibroDiario/${nroAsiento}/descontabilizar`);
  },

  /** Solo SUPERADMIN: corrige fecha en cabecera y líneas (incluye asientos contabilizados o vinculados). */
  patchFechaAsiento: async (nroAsiento: number, fechaAsiento: string): Promise<void> => {
    await api.patch(`/api/LibroDiario/${nroAsiento}/fecha-asiento`, { fechaAsiento });
  },

  /** PDF del libro diario con los mismos filtros que el listado (sin paginación). */
  getPreviewPdfBlob: async (params: LibroDiarioPdfParams): Promise<Blob> => {
    try {
      const response = await api.get<Blob>('/api/LibroDiario/preview-pdf', {
        params: {
          fechaDesde: params.fechaDesde || undefined,
          fechaHasta: params.fechaHasta || undefined,
          nroAsiento: params.nroAsiento ?? undefined,
          searchTerm: params.searchTerm?.trim() || undefined
        },
        responseType: 'blob'
      });
      return response.data;
    } catch (err: unknown) {
      const ax = err as { response?: { data?: Blob } };
      const blobErr = ax.response?.data;
      if (blobErr instanceof Blob && blobErr.size < 8192) {
        const t = await blobErr.text();
        try {
          const j = JSON.parse(t) as { message?: string };
          if (typeof j.message === 'string' && j.message.length > 0) {
            throw new Error(j.message);
          }
        } catch (e: unknown) {
          if (e instanceof SyntaxError) {
            /* no es JSON */
          } else if (e instanceof Error) {
            throw e;
          }
        }
      }
      throw new Error(unwrapMessage(err));
    }
  },

  parseError: unwrapMessage
};

export default libroDiarioService;
